import { usePage } from "@inertiajs/react"

import { useNavPermissions } from "@/hooks/use-nav-permissions"
import { isGroup } from "@/lib/nav-data"
import type { NavEntry } from "@/lib/nav-data"

export function useFilteredItems(
  sectionKey: string,
  items: NavEntry[],
): NavEntry[] {
  const perms = useNavPermissions()
  const { auth } = usePage<{
    auth: { user: { is_admin: boolean; permissions: Record<string, boolean> } }
  }>().props
  if (!auth?.user) return []
  if (perms.isAdmin) return items
  const p = auth.user.permissions || {}

  switch (sectionKey) {
    case "accounting":
      return items.filter((entry) => {
        if (isGroup(entry)) {
          if (entry.label === "Expenses") return !!p.payExpenses
          if (entry.label === "Payroll") return !!p.viewPayroll
        }
        if (!isGroup(entry)) {
          if (entry.label === "Search Expenses")
            return !!(p.approveTimeExpenses || p.payExpenses)
          if (entry.label === "YTD Case Summary") return false
        }
        return true
      })

    case "activity":
      return items.filter((entry) => {
        if (isGroup(entry)) {
          if (entry.label === "Time & Expenses")
            return perms.canApproveTimeExpenses
        }
        if (!isGroup(entry)) {
          if (entry.label === "Pending Reports")
            return perms.canApproveDailyReports
          if (entry.label === "Prepare Reports") return perms.canPrepareReports
          if (entry.label === "Search Reports")
            return perms.canApproveDailyReports
          if (entry.label === "Time Off Requests")
            return perms.canApproveTimeOff
        }
        return true
      })

    case "marketing":
      return items.filter((entry) => {
        if (isGroup(entry)) {
          if (entry.label === "Reports") return perms.canMktCategories
        }
        if (!isGroup(entry)) {
          if (entry.label === "Categories") return perms.canMktCategories
        }
        return true
      })

    case "equipment":
      return perms.isAdmin || perms.isFullAccess ? items : []

    case "admin":
      return perms.isAdmin ? items : []

    case "company":
      return items.filter((entry) => {
        if (!isGroup(entry)) {
          if (entry.label === "Documents") return perms.canUploadDocs
          if (entry.label === "Subcontractors") return perms.canViewVendors
        }
        return true
      })

    default:
      return items
  }
}
